import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { ArrowUpRight, Factory, PenTool, Wrench } from "lucide-react";
import LazyImage from "@/components/LazyImage";
import usePageSEOOverride from "@/hooks/useSEOOverrides";

/**
 * Products hub — 32 manufacturing categories.
 * Slugs map 1:1 to existing /products/:slug detail pages handled by ProductDetail.jsx.
 * Each card surfaces a real product image already used inside the detail page,
 * keeps the dark-hero / orange-accent design language from HomePage,
 * and avoids any unverified numerical claims for Google Ads compliance.
 */

const CATEGORIES = {
  workspaces: {
    title: "Workspaces",
    subhead: "Modular desking systems for daily work — designed to scale from a 50-seater floor to a multi-floor corporate campus.",
    items: [
      { slug: "linear-workstations", name: "Modular Office Linear Workstations", desc: "Modular linear desk systems for high-density floor layouts.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2ipzm9lu_45.png" },
      { slug: "corner-workstations", name: "Modular Office Corner Workstation", desc: "L-shaped corner desks that maximise floor corners and give each user more usable surface.", image: "/media/corner-workstations/corner-workstation-37.jpg" },
      { slug: "hot-desks", name: "Modular Office Hot Desks", desc: "Shared-use desks for hybrid teams and rotational seating.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6j4wvxn5_Screenshot%202026-03-26%20133049.png" },
      { slug: "acoustic-workstations", name: "Modular Office Acoustic Workstations", desc: "Desks with integrated acoustic panels for focus-heavy floors.", image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4s13te7v_Screenshot%202026-03-24%20162114-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer.png" },
      { slug: "height-adjustable-desks", name: "Modular Office Height Adjustable Desks", desc: "Sit-stand desks built for ergonomic compliance.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/31q3tjl7_Screenshot%202026-03-24%20160527.png" },
    ],
  },
  executive: {
    title: "Executive & Leadership",
    subhead: "Workspaces for C-suite and leadership — substantial in presence, exacting in detail.",
    items: [
      { slug: "executive-desks", name: "Modular Office Executive Desks", desc: "Statement desks for cabin and corner-office layouts.", image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0h39uvqs_Pic%2018.png" },
      { slug: "executive-chairs", name: "Modular Office Executive Chairs", desc: "High-back ergonomic seating engineered for long working hours.", image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/q2fhpp2z_imgi_15_Bruno-highback.png" },
    ],
  },
  meeting: {
    title: "Meeting & Collaboration",
    subhead: "Discussion-first environments — from quick 2-person huddles to formal multi-stakeholder reviews.",
    items: [
      { slug: "meeting-tables", name: "Modular Office Meeting Tables", desc: "Compact tables for 4-8 seater meeting rooms.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/m6mu122m_Screenshot%202026-03-25%20172811.png" },
      { slug: "meeting-pods", name: "Modular Office Meeting Pods", desc: "Enclosed acoustic pods for private discussions on the floor.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/a32nngo8_Screenshot%202026-03-24%20145827.png" },
      { slug: "meeting-booths", name: "Modular Office Meeting Booths", desc: "Semi-enclosed booths for informal 1-on-1 meetings.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ox4oj6lq_Screenshot%202026-03-24%20162232.png" },
    ],
  },
  privacy: {
    title: "Privacy & Focus",
    subhead: "Solutions that carve quiet, distraction-free zones inside open-plan floors.",
    items: [
      { slug: "phone-booths", name: "Modular Office Phone Booths", desc: "Acoustic single-person pods for calls and video meetings.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/k23r4zhn_Screenshot%202026-03-24%20150513.png" },
      { slug: "privacy-screens", name: "Modular Office Privacy Screens", desc: "Desk-mounted fabric screens for visual separation.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/o1y9ibk5_Fabric%20Privacy%20Screen.png" },
      { slug: "cubicle-systems", name: "Modular Office Cubicle Systems", desc: "Partitioned workstations for high-concentration roles.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/90bms87p_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2003_33_52%20PM.png" },
    ],
  },
  partitions: {
    title: "Partitions, Receptions & Space Division",
    subhead: "Single, double, grid-framed partitions, modular walls, reception counters and training rooms — the architectural envelope of your floor.",
    items: [
      { slug: "glass-partitions", name: "Modular Office Cabin Single, Double & Grid Framed Partitions", desc: "Slim single-glazed, acoustic double-glazed and industrial-grid framed glass partitions for cabins and meeting rooms — one modular partition system, three variants.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oha52fut_Screenshot%202026-03-26%20161944.png" },
      { slug: "modular-walls", name: "Modular Office Wall Partitions", desc: "Demountable wall systems for fast reconfiguration.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/yryukeo5_Modular%20Walls%202.png", fit: "contain" },
      { slug: "reception-counters", name: "Modular Office Reception Counters", desc: "First-impression reception desks in laminate, veneer or solid surface.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/nzjaj9qj_Screenshot%202026-03-16%20135949.png" },
      { slug: "training-room-setup", name: "Modular Office Training Room Setup", desc: "Flip-top desks, stackable chairs and complete training-room kits.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ae48tr70_Screenshot%202026-03-25%20121014.png" },
    ],
  },
  storage: {
    title: "Storage, Breakout Furniture & Specialty",
    subhead: "Storage systems, breakout furniture, and verticals built for banking, public sector and customer-facing floors.",
    items: [
      { slug: "filing-cabinets", name: "Modular Office Filing Cabinets", desc: "Lockable filing cabinets in melamine and metal finishes.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/se8uej32_Screenshot%202026-03-26%20121641.png" },
      { slug: "teller-counters", name: "Modular Office Teller Counters", desc: "Banking-grade teller counters with cash-handling integration.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/llma8rb8_imgi_12_IMG_0308.png" },
      { slug: "customer-seating", name: "Modular Office Customer Seating", desc: "Lobby and waiting-area seating for banks, clinics and offices.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/axrjgzyi_Gemini_Generated_Image_d58n7vd58n7vd58n.png" },
      { slug: "breakout-furniture", name: "Modular Office Breakout Furniture", desc: "Lounge seating, ottomans and casual tables for breakout zones.", image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ca7f8j23_Screenshot%202026-03-26%20165922.png" },
    ],
  },
};

const TOTAL_COUNT = Object.values(CATEGORIES).reduce((s, g) => s + g.items.length, 0);

const CapabilityIcon = ({ Icon, title, body }) => (
  <div className="flex flex-col items-start gap-4 p-8 border border-[#E2E8F0] bg-white">
    <div className="w-12 h-12 flex items-center justify-center bg-[#0F172A] text-[#F97316]">
      <Icon className="w-6 h-6" strokeWidth={1.5} />
    </div>
    <h3 className="text-lg font-semibold text-[#0F172A]">{title}</h3>
    <p className="text-sm text-[#475569] leading-relaxed">{body}</p>
  </div>
);

export default function ProductsPage() {
  usePageSEOOverride("/products", {
    canonical: "https://ezeefitofficesystems.in/products",
    description: `${TOTAL_COUNT} modular office furniture categories by Ezeefit — workstations, desks, conference tables, partitions, phone booths & storage. Factory-direct.`,
  });

  const allItems = Object.values(CATEGORIES).flatMap((g) => g.items);
  const itemListJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Ezeefit Office Furniture — Product Range",
    "description": `${TOTAL_COUNT} modular office furniture categories manufactured, delivered and installed by Ezeefit Office Systems from a single Bhiwandi (Thane) factory since 2014. Every category is factory-direct with ISO 9001:2015 certified quality and pan-India installation.`,
    "itemListOrder": "https://schema.org/ItemListOrderAscending",
    "numberOfItems": allItems.length,
    "itemListElement": allItems.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "url": `https://ezeefitofficesystems.in/products/${it.slug}`,
      "name": it.name,
      "description": it.desc,
      "image": it.image?.startsWith("http") ? it.image : `https://ezeefitofficesystems.in${it.image}`,
    })),
  });
  const breadcrumbJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in/" },
      { "@type": "ListItem", "position": 2, "name": "Products", "item": "https://ezeefitofficesystems.in/products" },
    ],
  });

  return (
    <div className="pt-20" data-testid="products-page">
      <Helmet>
        <title>Office Modular Workstations &amp; Corporate Office Furniture Solutions | Ezeefit Mumbai</title>
        <meta name="description" content="Mumbai's premier office furniture manufacturer for tier-1 corporate spaces. High-density modular workstations, acoustic phone booths, executive cabin partitions & turnkey commercial fit-outs — engineered precision, custom CAD support, pan-India installation from our Bhiwandi manufacturing hub." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/products" />
        <meta property="og:title" content="Office Modular Workstations &amp; Corporate Office Furniture Solutions | Ezeefit" />
        <meta property="og:description" content="Partner directly with Mumbai's premier office furniture manufacturer for tier-1 corporate spaces. Custom CAD support, engineered precision & end-to-end site installation across India." />
        <meta property="og:url" content="https://ezeefitofficesystems.in/products" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: itemListJsonLd }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbJsonLd }} />
      </Helmet>

      {/* HERO — matches homepage dark/orange aesthetic */}
      <section className="relative bg-[#0F172A] noise-overlay overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, #F97316 0%, transparent 40%), radial-gradient(circle at 80% 70%, #F97316 0%, transparent 40%)",
        }} />

        {/* SIDE COLLAGES — 21 product hero images, vertical marquee, edge margins + smaller tiles.
            Extra left/right offset clears the floating action panel + WhatsApp/chat FABs. */}
        <div
          className="pointer-events-none absolute top-6 bottom-24 left-16 lg:left-24 w-[9vw] max-w-[110px] hidden md:block opacity-90 overflow-hidden"
          style={{ maskImage: "linear-gradient(180deg, transparent 0%, #000 15%, #000 85%, transparent 100%)", WebkitMaskImage: "linear-gradient(180deg, transparent 0%, #000 15%, #000 85%, transparent 100%)" }}
          aria-hidden="true"
        >
          <div className="flex flex-col gap-2 animate-marquee-y">
            {[...Object.values(CATEGORIES).flatMap(g => g.items), ...Object.values(CATEGORIES).flatMap(g => g.items)].map((item, i) => (
              <div key={`L-${i}`} className="relative rounded-lg overflow-hidden aspect-square shadow-md ring-1 ring-white/10">
                <img src={item.image} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 via-transparent to-transparent" />
              </div>
            ))}
          </div>
        </div>
        <div
          className="pointer-events-none absolute top-6 bottom-24 right-16 lg:right-24 w-[9vw] max-w-[110px] hidden md:block opacity-90 overflow-hidden"
          style={{ maskImage: "linear-gradient(180deg, transparent 0%, #000 15%, #000 85%, transparent 100%)", WebkitMaskImage: "linear-gradient(180deg, transparent 0%, #000 15%, #000 85%, transparent 100%)" }}
          aria-hidden="true"
        >
          <div className="flex flex-col gap-2 animate-marquee-y-reverse">
            {[...Object.values(CATEGORIES).flatMap(g => g.items).slice().reverse(), ...Object.values(CATEGORIES).flatMap(g => g.items).slice().reverse()].map((item, i) => (
              <div key={`R-${i}`} className="relative rounded-lg overflow-hidden aspect-square shadow-md ring-1 ring-white/10">
                <img src={item.image} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 via-transparent to-transparent" />
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-28 lg:px-36 py-24 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)] gap-10 lg:gap-14 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-3 px-4 py-2 border border-[#F97316]/40 mb-8" data-testid="products-eyebrow">
                <span className="w-2 h-2 bg-[#F97316] rounded-full" />
                <span className="text-xs font-mono tracking-widest uppercase text-[#F97316]">
                  Commercial Fit-Out Partner · Mumbai Manufacturing & Supply Hub · Pan India Delivery
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-8 max-w-4xl" data-testid="products-h1">
                Office Modular Workstations &amp; <span className="text-[#F97316]">Corporate Office Furniture Solutions</span>
              </h1>

              <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-3xl mb-6">
                Partner directly with Mumbai&rsquo;s premier office furniture manufacturer for tier-1 corporate spaces. From high-density linear workstations to acoustic phone booths and partition for executive cabin, Ezeefit delivers engineered precision, custom CAD support, and end-to-end site installation across India.
              </p>
              <p className="text-sm text-[#64748B] max-w-3xl">
                Select a product category to review technical details and project portfolios.
              </p>
            </motion.div>

            {/* Right-side headline list — 21 categories with sub-descriptor */}
            <motion.aside
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:pt-2 lg:max-h-[560px] lg:overflow-y-auto pr-1 nice-scroll"
              aria-label="Complete product catalog"
              data-testid="hero-category-list"
            >
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#F97316] mb-3">The 21-Category Range</p>
              <ul className="space-y-2.5">
                {Object.values(CATEGORIES).flatMap(g => g.items).map((item, i) => (
                  <li key={`hl-${item.slug}`} className="group">
                    <Link
                      to={`/products/${item.slug}`}
                      state={{ fromProducts: true }}
                      className="flex items-baseline gap-2.5 py-1 border-b border-white/5 hover:border-[#F97316]/40 transition-colors"
                    >
                      <span className="text-[10px] font-mono text-[#F97316] tabular-nums shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-semibold text-white group-hover:text-[#F97316] transition-colors leading-snug">{item.name}</span>
                        <span className="block text-[11px] text-[#64748B] group-hover:text-[#94A3B8] transition-colors leading-snug line-clamp-2">{item.desc}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.aside>
          </div>
        </div>
      </section>

      {/* CATEGORY GROUPS */}
      <section className="bg-[#F8FAFC] py-24 lg:py-32" data-testid="products-grid-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {Object.entries(CATEGORIES).map(([key, group], gIdx) => (
            <motion.div
              key={key}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: gIdx * 0.05 }}
              data-testid={`product-group-${key}`}
              className={gIdx === Object.keys(CATEGORIES).length - 1 ? "" : "pb-14 lg:pb-20 mb-14 lg:mb-20 border-b border-[#E2E8F0]/60"}
            >
              {/* Group header */}
              <div className="flex items-baseline justify-between border-b border-[#E2E8F0] pb-6 mb-14">
                <div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight" data-testid={`product-group-title-${key}`}>
                    {group.title}
                  </h2>
                  <p className="text-sm text-[#475569] mt-3 max-w-2xl leading-relaxed">{group.subhead}</p>
                </div>
                <span className="text-xs font-mono tracking-widest uppercase text-[#94A3B8] whitespace-nowrap hidden sm:block">
                  {String(group.items.length).padStart(2, "0")} Categories
                </span>
              </div>

              {/* Cards grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {group.items.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/products/${item.slug}`}
                    state={{ fromProducts: true }}
                    data-testid={`product-card-${item.slug}`}
                    className="group bg-white border border-[#E2E8F0] hover:border-[#F97316] transition-colors duration-300 overflow-hidden flex flex-col"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-[#F1F5F9]">
                      <LazyImage
                        src={item.image}
                        alt={`${item.name} — Ezeefit Office Systems`}
                        containerClassName="w-full h-full"
                        className={`w-full h-full ${item.fit === "contain" ? "object-contain p-3" : "object-cover"} group-hover:scale-105 transition-transform duration-500`}
                      />
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="text-base font-semibold text-[#0F172A] leading-snug group-hover:text-[#F97316] transition-colors">
                          {item.name}
                        </h3>
                        <ArrowUpRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#F97316] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                      </div>
                      <p className="text-sm text-[#64748B] leading-relaxed">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY MANUFACTURE — reinforces homepage promise */}
      <section className="bg-white py-20 lg:py-24 border-t border-[#E2E8F0]" data-testid="products-why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-4 block">Why Ezeefit</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight leading-tight">
              One factory. One team.<br />
              <span className="text-[#475569]">Complete execution under one roof.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <CapabilityIcon
              Icon={PenTool}
              title="CAD Design In-House"
              body="Floor plans, 3D layouts and BOQs are produced by our internal design team — not outsourced — so what you approve is exactly what gets manufactured."
            />
            <CapabilityIcon
              Icon={Factory}
              title="Manufactured in Bhiwandi"
              body="Our Bhiwandi, Thane factory runs CNC machinery, edge banders and panel saws. Every component is built in-house under ISO 9001:2015 quality controls."
            />
            <CapabilityIcon
              Icon={Wrench}
              title="Installed by Ezeefit Teams"
              body="No third-party subcontractors. Our installation crews execute the project on-site — from delivery and assembly to final hand-over."
            />
          </div>
        </div>
      </section>

      {/* PROJECT CTA strip */}
      <section className="bg-[#0F172A] py-16 lg:py-20" data-testid="products-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight leading-tight mb-3">
              Planning an office project?
            </h2>
            <p className="text-[#94A3B8] text-base leading-relaxed">
              Share your seat count, location and timeline — our team will revert with a layout proposal, finish samples and a detailed quotation. Typical response within one business day.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <Link
              to="/contact"
              data-testid="products-cta-enquiry"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold tracking-wide uppercase transition-colors"
            >
              Request Project Quote
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.25} />
            </Link>
            <a
              href="tel:+919820596811"
              data-testid="products-cta-call"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#334155] text-white hover:border-[#F97316] hover:text-[#F97316] text-sm font-semibold tracking-wide uppercase transition-colors"
            >
              Call <span className="phone-flash-dark">9820596811</span> <span className="text-[#94A3B8] font-normal normal-case">or</span> <span>9820042100</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
